import type { ReactElement } from 'react';
import type { ReferenceTitleDto } from '@gr/core/content/entities/dto';
import { FieldType, ReferenceTargetKind, type FieldDefinition, type FieldValue } from '@gr/core/content/entities/fields';
import { Link, useT } from '../../../../app/di';
import { formatDate, stars } from '../../../../shared/format';

export interface FieldValueViewProps {
  blogId: string;
  field: FieldDefinition;
  value: FieldValue | undefined;
  /** Заголовки целей ссылок — чтобы показать их без запросов. */
  titles: Readonly<Record<string, ReferenceTitleDto>>;
}

/** Отображение значения пользовательского поля; ссылки открываются в один клик (ENT-UC-8). */
export function FieldValueView({ blogId, field, value, titles }: FieldValueViewProps): ReactElement {
  const { t, locale } = useT();
  if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) return <span className="u-muted">{t('common.none')}</span>;
  switch (field.type) {
    case FieldType.Boolean:
      return <span>{value ? t('common.yes') : t('common.no')}</span>;
    case FieldType.Rating:
      return <span title={String(value)}>{stars(Number(value))}</span>;
    case FieldType.Date:
      return <span>{formatDate(`${String(value)}T00:00:00Z`, locale, 'UTC')}</span>;
    case FieldType.Url:
      return (
        <a href={String(value)} target="_blank" rel="noreferrer noopener">
          {String(value)}
        </a>
      );
    case FieldType.Select:
      return <span>{Array.isArray(value) ? value.join(', ') : String(value)}</span>;
    case FieldType.Reference: {
      const ids = Array.isArray(value) ? value : [String(value)];
      return (
        <span className="u-row">
          {ids.map((id) => {
            const ref = titles[id];
            if (!ref) return <span key={id} className="u-muted">{id}</span>;
            return ref.kind === ReferenceTargetKind.Post ? (
              <Link key={id} to="post" params={{ blogId, postId: id }}>
                {ref.title}
              </Link>
            ) : (
              <Link key={id} to="entity" params={{ blogId, typeId: ref.entityTypeId ?? '', entityId: id }}>
                {ref.title}
              </Link>
            );
          })}
        </span>
      );
    }
    default:
      return <span className="u-pre">{String(value)}</span>;
  }
}
